import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import './DRResourceDetail.css';
import EditIcon from '../../assets/icons/edit.svg?react';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import InfoIcon from '../../assets/icons/info-circle.svg?react';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import { useDRResourceDetail } from '../../hooks/useDRResourceDetail';
import StationAccordion from './components/StationAccordion';
import StationAssignSideModal from './components/StationAssignSideModal';
import StationBulkAssignModal from './components/StationBulkAssignModal';
import DRUpdateSideModal from './components/DRUpdateSideModal';
import DRDeleteModal from '../DRManagement/component/DRDeleteModal';
import AlgoSelectModal from './components/AlgoSelectModal';
import { useUpdateDRResource } from '../../hooks/queries/drResourceQueries';
import DrResourceChart from './components/DrResourceChart';
import DrResourceBarChart from './components/DrResourceBarChart';
import useRealtimeScheduler from '../../hooks/useRealtimeScheduler';
import SearchBox from '../../components/SearchBox/SearchBox';
import { isSystemAdmin } from '../../utils/permissions';

const DRResourceDetail = () => {
  const { drResourceSysId } = useParams();
  const isSystemAdminUser = isSystemAdmin();
  const {
    drResource, 
    isLoading, 
    error,
    getDRResourceDetail,
    defaultCblModuleName,
    defaultCblModuleVersion,
    predictionCblData,
    powerMeterDemandData,
    livePowerMeterDemandData,
    liveCblData,
    drEventPowerMeterDemandData,
    drHistoryLatestData,
    refetchAll,
    defaultChargingDemandPredictionModuleName,
    defaultChargingDemandPredictionModuleVersion,
    predictionChargingDemandData,
    getPredictionChargingDemand,
  } = useDRResourceDetail(drResourceSysId);
  
  // 실시간 스케줄러
  const {
    isUpdating,
    lastUpdateTime,
    nextUpdateTime,
    manualUpdate,
  } = useRealtimeScheduler(async (updateTime) => {
    refetchAll();
  },
  {
    enabled: !!drResourceSysId, // DR 자원 ID가 있을 때 활성화
    immediate: true // 초기 로드 시 즉시 실행
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('stat');
  const [chargerCompModalOn, setChargerCompModalOn] = useState(false);
  const [stationAssignModalOn, setStationAssignModalOn] = useState(false);
  const [stationBulkAssignModalOn, setStationBulkAssignModalOn] = useState(false);
  const [editModalOn, setEditModalOn] = useState(false);
  const [deleteModalOn, setDeleteModalOn] = useState(false);
  const [algoSelectModalOn, setAlgoSelectModalOn] = useState(false);
  const [canSearch, setCanSearch] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const searchBoxRef = useRef(null);

  const updateDRResource = useUpdateDRResource();

  // 뒤로가기 핸들러
  const handleBack = () => {
    if (location.state?.from) {
      navigate(-1); // 프로젝트 내에서 접근한 경우 뒤로가기
    } else {
      navigate('/dr-management'); // URL로 직접 접근한 경우 목록으로
    }
  };
  
  const handleStationSearch = (value) => {
    setSearchValue(value);
  };

  const handleStationAssign = () => {
    getDRResourceDetail();
  };

  const onEditSuccess = () => {
    // 페이지 새로고침으로 최신 데이터 로드
    window.location.reload();
  };

  const onDeleteSuccess = () => {
    // 삭제 성공 시 목록 페이지로 이동
    navigate('/dr-management');
  };

  const onPutAlgoToDrResource = (algo, onSuccessCallback) => {
    updateDRResource.mutate({
      data: {
        ...drResource,
        AppliiedCblAlgorithm: algo.ModuleName,
        AppliedCblAlgorithmVersion: algo.Version,
      },
    }, {
      onSuccess: () => {
        // 업데이트 성공 후 데이터 새로고침
        getDRResourceDetail();
        setAlgoSelectModalOn(false);
        // 추가 성공 콜백 실행
        if (onSuccessCallback) {
          onSuccessCallback();
        }
      }
    });
  };


  if (isLoading) {
    return (
      <div className="detail-loading">
        <div className="loading-spinner"></div>
        <p className='text-b1-400 text-base'>DR 자원 정보를 불러오는 중...</p>
      </div>
    );
  }

  if (!drResource) {
    return (
      <div className="detail-not-found">
        <h2 className='text-b1-700 text-base'>DR 자원을 찾을 수 없습니다</h2>
        <p className='text-b1-400 text-base'>요청하신 DR 자원이 존재하지 않거나 삭제되었습니다.</p>
        <button onClick={() => navigate('/dr-management')} className="btn big-btn btn-primary">
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="detail-page-container">
      <div className="detail-page-content">
          <div className='detail-page-content-divide-container'>
            <div className='detail-page-content-divide-content left'>
              {/* LEFT HEADER */}
              <div className='flex align-center mb-5'>
                <button className='btn icon-btn' onClick={handleBack} title="뒤로가기">
                  <ChevronLeftIcon color='var(--gray-600)'/>
                  <span className='text-b2-700 text-gray-600'>뒤로가기</span>
                </button>
              </div>
              <div className='detail-page-content-divide-content-header flex align-center gap-5'>
                <h3 className='text-b1-700 text-primary'>{drResource.ResourceName}</h3>
                <button className='btn icon-btn' onClick={() => setEditModalOn(true)}>
                  <EditIcon color='var(--gray-600)'/>
                </button>
                <button className='btn icon-btn' onClick={() => setDeleteModalOn(true)}>
                  <DeleteIcon color='var(--gray-600)'/>
                </button>
              </div>
              <p className="text-b2-400 text-gray-600 mt-2">DR 자원 정보 상세 조회</p>
              {/* LEFT CONTENT */}
              <div className='flex flex-col gap-10 mt-10 mb-20'>
                <h3 className='text-b1-700 text-base'>기본 정보</h3>
                <table className='gray-table'>
                  <tr>
                    <th width="120px">DR 자원 이름</th>
                    <td>{drResource.ResourceName}</td>
                  </tr>
                  <tr>
                    <th width="120px">지역구분코드</th>
                    <td>{drResource.RegionCode}</td>
                  </tr>
                  <tr>
                    <th width="120px">시도</th>
                    <td>{drResource.RegionSido}</td>
                  </tr>
                  <tr>
                    <th width="120px">시군구</th>
                    <td>{drResource.RegionSigungu}</td>
                  </tr>
                  <tr>
                    <th width="120px">읍면동</th>
                    <td>{drResource.RegionEupMyuonDong}</td>
                  </tr>
                  <tr>
                    <th width="120px">참여 DR제도</th>
                    <td>{drResource.DrType}</td>
                  </tr>
                </table>
              </div>
              
              <div className='flex flex-col gap-10 mt-10 mb-20'>
                <div className='flex align-center justify-between'>
                  <h3 className='text-b1-700 text-base'>CBL 연산 모듈 정보</h3>
                  <button className='btn small-btn btn-primary' onClick={() => setAlgoSelectModalOn(true)}>연산 모듈 선택</button>
                </div>
                <table className='gray-table'>
                  <tr>
                    <th width="120px">알고리즘 이름</th>
                    <td>{drResource.AppliiedCblAlgorithm || `(기본) ${defaultCblModuleName}` || "-"}</td>
                  </tr>
                  <tr>
                    <th width="120px">버전</th>
                    <td>{drResource.AppliedCblAlgorithmVersion || defaultCblModuleVersion || "-"}</td>
                  </tr>
                </table>
              </div>
              
              <div className='flex flex-col gap-10 mt-10 mb-20'>
                <div className='flex flex-col gap-2'>
                  <h3 className='text-b1-700 text-base'>일일 충전량 예측 연산 모듈 정보</h3>
                  <p className='text-b2-400 text-gray-600'>적용되는 모듈은 시스템 설정을 통해 변경 가능합니다.</p>
                  {/* <button className='btn small-btn btn-primary' onClick={() => setAlgoSelectModalOn(true)}>연산 모듈 선택</button> */}
                </div>
                <table className='gray-table'>
                  <tr>
                    <th width="120px">모델 이름</th>
                    <td>{defaultChargingDemandPredictionModuleName || "-"}</td>
                  </tr>
                  <tr>
                    <th width="120px">버전</th>
                    <td>{defaultChargingDemandPredictionModuleVersion || "-"}</td>
                  </tr>
                </table>
              </div>
              
              
              
            
              

            </div>
            <div className='detail-page-content-divide-content right'>
              {/* RIGHT CONTENT */}
              <div className="dr-resource-detail-tabs-container">
                <div className="tabs dr-resource-detail-tabs">
                  <button
                    className={`tab ${activeTab === 'stat' ? 'active' : ''}`}
                    onClick={() => setActiveTab('stat')}
                  >
                    통계
                  </button>
                  <button
                    className={`tab ${activeTab === 'station' ? 'active' : ''}`}
                    onClick={() => setActiveTab('station')}
                  >
                    충전소-충전기 목록
                  </button>
                  <button
                    className={`tab ${activeTab === 'prediction' ? 'active' : ''}`}
                    onClick={() => setActiveTab('prediction')}
                  >
                    일일 충전량 예측
                  </button>
                </div>
              </div>
              <div className='flex flex-col gap-10 mb-20'>
                {activeTab === 'stat' && (
                  <div className='flex flex-col gap-10 mb-20'>
                    <div className='flex flex-col gap-10'>
                      {/* <h3 className='text-b1-700 text-base'>충전소 과거 24시간 전력 수요 이력</h3> */}
                      <DrResourceChart
                        type='power'
                        dashboardData={{
                          powerMeterDemandData,
                          livePowerMeterDemandData,
                          liveCblData,
                          drEventPowerMeterDemandData,
                          drHistoryLatestData,
                          lastUpdateTime,
                          nextUpdateTime,
                          isUpdating,
                          manualUpdate,
                          isLoading
                        }}
                      />
                    </div>
                    <div className='flex flex-col gap-10'>
                      {/* <h3 className='text-b1-700 text-base'>충전소 과거 24시간 전력 수요 이력</h3> */}
                      <DrResourceChart
                        type='cbl'
                        dashboardData={{
                          predictionCblData,
                          powerMeterDemandData,
                          livePowerMeterDemandData,
                          liveCblData,
                          drEventPowerMeterDemandData,
                          drHistoryLatestData,
                          lastUpdateTime,
                          nextUpdateTime,
                          isUpdating,
                          manualUpdate,
                          isLoading
                        }}
                      />
                    </div>
                    <div className='flex flex-col gap-10'>
                      {/* <h3 className='text-b1-700 text-base'>충전소 과거 24시간 전력 수요 이력</h3> */}
                      <DrResourceChart
                        type='dr'
                        dashboardData={{
                          predictionCblData,
                          powerMeterDemandData,
                          livePowerMeterDemandData,
                          liveCblData,
                          drEventPowerMeterDemandData,
                          drHistoryLatestData,
                          lastUpdateTime,
                          nextUpdateTime,
                          isUpdating,
                          manualUpdate,
                          isLoading
                        }}
                      />
                    </div>
                  </div>
                )}
                {activeTab === 'station' && (
                  <div className='flex flex-col gap-10 mb-20'>
                    <div className="flex align-center justify-between">
                      <div className='flex align-center gap-10'>
                        <div className='flex align-center gap-2 text-b2-400 text-gray-600'>
                          충전소
                          <p className='text-b2-700 text-primary'>{drResource?.Stations?.length}</p>
                        </div>
                        <div className='flex align-center gap-2 text-b2-400 text-gray-600'>
                          충전기
                          <p className='text-b2-700 text-primary'>{drResource?.Chargers?.length}</p>
                        </div>
                      </div>
                      <div className='flex align-center gap-5'>
                        {isSystemAdminUser && (
                          <button className='btn big-btn btn-style-2' onClick={() => setStationBulkAssignModalOn(true)}>충전소 일괄 할당</button>
                        )}
                        <button 
                          className='btn big-btn btn-primary'
                          onClick={() => setStationAssignModalOn(true)}
                        >
                          충전소 할당 편집
                        </button>
                      </div>
                    </div>
                    {/* 검색 */}
                    <div>
                      <SearchBox
                        ref={searchBoxRef}
                        placeholder="충전소 검색 - 이름 또는 계량기 계약번호"
                        onSearch={handleStationSearch}
                        showFilter={false} 
                        onSearchStateChange={setCanSearch}
                        defaultValue={searchValue}
                      />
                    </div>
                    <StationAccordion 
                      stations={drResource?.Stations?.filter(station => station.CpoStationName.includes(searchValue) || station.PowerMeterContractNumber.includes(searchValue)) || []}
                      onChargerSelect={(charger, station) => {
                        // 충전기 선택 시 처리 로직
                      }}
                    />
                  </div>
                )}
                {activeTab === 'prediction' && (
                  <div className='flex flex-col gap-5 mb-20'>
                    <h3 className='text-b1-700 text-base'>최근 7일 충전량 예측값</h3>
                    <DrResourceBarChart
                      type='prediction'
                      data={predictionChargingDemandData}
                      metaData={drResource}
                    />
                    <div className="chart-description-card">
                      <div className="chart-description-header">
                        <InfoIcon color='var(--gray-600)'/>
                        <h4 className="chart-description-title">일일 충전량 예측이란?</h4>
                      </div>
                      <p className="chart-description-text">
                        일일 충전량 예측은 <strong>충전소별로 일 단위 충전 수요를 예측하는 기능</strong>으로, 과거 충전 이력, 날씨, 요일, 관광지 특성, 주변 상권(POI) 등의 다양한 데이터를 기반으로 <strong>일일 총 충전량(kWh)과 충전횟수(건수)</strong>를 산출합니다.
                      </p>
                      <p className="chart-description-text">
                        이를 통해 해당 충전소에서 하루 동안 얼마나 많은 충전이 발생할지를 사전에 파악할 수 있습니다.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* 충전소 할당 모달 */}
        {stationAssignModalOn && (
          <StationAssignSideModal
            onClose={() => setStationAssignModalOn(false)}
            drResource={drResource}
            onAssign={handleStationAssign}
          />
        )}
        
        {/* 수정 모달 */}
        {editModalOn && (
          <DRUpdateSideModal 
            onClose={() => setEditModalOn(false)} 
            target={drResource} 
            onSuccess={onEditSuccess} 
          />
        )}
        
        {/* 삭제 모달 */}
        {deleteModalOn && (
          <DRDeleteModal 
            onClose={() => setDeleteModalOn(false)} 
            drResource={drResource} 
            onDeleteSuccess={onDeleteSuccess} 
          />
        )}

        {/* 알고리즘 선택 모달 */}
        {algoSelectModalOn && (
          <AlgoSelectModal
            onClose={() => setAlgoSelectModalOn(false)}
            currentAlgo={drResource.AppliiedCblAlgorithm || defaultCblModuleName}
            currentAlgoVersion={drResource.AppliedCblAlgorithmVersion || defaultCblModuleVersion}
            onPutAlgoToDrResource={onPutAlgoToDrResource}
          />
        )}

        {/* 충전소 일괄 할당 모달 */}
        {stationBulkAssignModalOn && (
          <StationBulkAssignModal
            onClose={() => setStationBulkAssignModalOn(false)}
            drResource={drResource}
            onSuccess={handleStationAssign}
          />
        )}
    </div>
  );
};

export default DRResourceDetail;
